import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Copy, Check } from 'lucide-react'
import {
  OnboardingStatusValue,
  type OnboardingPersonalizationResponse,
  type OnboardingPrompt,
} from '@super/shared-types'
import { Button } from '@/components/ui/button'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import { onboardingClient } from '@/lib/api/onboarding.client'
import { ApiKeysClient } from '@/lib/api/api-keys.client'
import { useAppAuth } from '@/contexts/app-auth.context'
import { useOnboarding } from '../onboarding-context'

const apiKeysClient = new ApiKeysClient()

interface MintedKey {
  apiKey: string
  keyLastFour: string | null
}

export function BuilderEndingStep() {
  const { state, patch } = useOnboarding()
  const navigate = useNavigate()
  const { refetchAppContext } = useAppAuth()
  const [finishing, setFinishing] = useState(false)
  const [personalization, setPersonalization] =
    useState<OnboardingPersonalizationResponse | null>(null)
  const [key, setKey] = useState<MintedKey | null>(null)
  const [keyMintError, setKeyMintError] = useState<string | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  useEffect(() => {
    onboardingClient
      .getPersonalization()
      .then(setPersonalization)
      .catch(() => setPersonalization({ prompts: [], starterAgentIds: [] }))
  }, [])

  useEffect(() => {
    let cancelled = false
    apiKeysClient
      .createApiKey({ name: 'Onboarding key' })
      .then(res => {
        if (cancelled) return
        setKey({ apiKey: res.apiKey, keyLastFour: res.keyLastFour })
      })
      .catch(err => {
        if (cancelled) return
        setKeyMintError(err instanceof Error ? err.message : 'unknown error')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleFinish = async () => {
    setFinishing(true)
    try {
      await patch({ status: OnboardingStatusValue.COMPLETED })
      await refetchAppContext()
      navigate('/dashboard', { replace: true })
    } catch {
      setFinishing(false)
    }
  }

  const handleCopy = async (id: string, text: string) => {
    await navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(prev => (prev === id ? null : prev)), 1200)
  }

  const maskedKey = key
    ? `sk_super_••••••••••${key.keyLastFour ?? '____'}`
    : 'minting...'
  const companyName = state.name ?? 'your company'

  return (
    <div className="mx-auto flex w-full max-w-[720px] flex-col gap-8 px-4">
      <div>
        <p className="text-muted-foreground font-fragment-mono text-[10px] uppercase tracking-[0.14em]">
          SUPER · SDK
        </p>
        <h1 className="font-space-grotesk mt-2 text-3xl font-semibold tracking-tight">
          Make your first call
        </h1>
        <p className="text-muted-foreground mt-2 text-sm">
          {companyName} is provisioned. Install the SDK, drop in your key, and
          wire one of the prompts below.
        </p>
      </div>

      <div className="rounded-lg bg-zinc-900 p-4 font-mono text-sm text-zinc-100">
        $ pnpm add @super-communications/sdk@latest
      </div>

      <div className="border-border rounded-lg border p-4">
        <div className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
          API key
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          <code className="font-mono text-sm">{maskedKey}</code>
          {key && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleCopy('apikey', key.apiKey)}
            >
              {copiedId === 'apikey' ? (
                <>
                  <Check className="mr-1 h-3 w-3" /> COPIED
                </>
              ) : (
                <>
                  <Copy className="mr-1 h-3 w-3" /> Copy
                </>
              )}
            </Button>
          )}
        </div>
        {keyMintError && (
          <p className="mt-2 text-xs text-red-500">
            Couldn't mint a key — {keyMintError}. Reload to try again.
          </p>
        )}
      </div>

      <div className="flex flex-col gap-3">
        <div className="text-muted-foreground text-[10px] font-medium uppercase tracking-wider">
          Prompts for you
        </div>
        {personalization?.prompts.map(prompt => (
          <PromptCard
            key={prompt.id}
            prompt={prompt}
            copiedId={copiedId}
            onCopy={handleCopy}
          />
        ))}
        {!personalization && (
          <div className="text-muted-foreground text-sm">Loading prompts…</div>
        )}
      </div>

      <FloatingActionIslandContent
        show={true}
        componentId="onboarding-done-builder"
        priority={10}
      >
        <div className="pointer-events-auto">
          <Button onClick={handleFinish} size="sm" disabled={finishing}>
            {finishing ? 'Finishing…' : 'Finish'}
          </Button>
        </div>
      </FloatingActionIslandContent>
    </div>
  )
}

interface PromptCardProps {
  prompt: OnboardingPrompt
  copiedId: string | null
  onCopy: (id: string, text: string) => void
}

function PromptCard({ prompt, copiedId, onCopy }: PromptCardProps) {
  return (
    <div className="border-border rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-medium">{prompt.title}</div>
          <p className="text-muted-foreground mt-1 text-xs">
            {prompt.description}
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-[10px] uppercase tracking-wider">
            <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-zinc-700">
              {prompt.trigger}
            </span>
            {prompt.integrationAffinity.slice(0, 2).map(id => (
              <span
                key={id}
                className="rounded-full bg-emerald-50 px-2 py-0.5 text-emerald-700"
              >
                {id}
              </span>
            ))}
          </div>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onCopy(prompt.id, prompt.snippet)}
        >
          {copiedId === prompt.id ? (
            <>
              <Check className="mr-1 h-3 w-3" /> COPIED
            </>
          ) : (
            <>
              <Copy className="mr-1 h-3 w-3" /> Copy
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
